/**
 * Detect if the user is on a mobile/tablet device
 * Returns true if mobile/tablet, false if desktop/laptop
 */
export const isMobileOrTablet = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent;
  const userAgentData = (navigator as Navigator & {
    userAgentData?: { mobile?: boolean; platform?: string };
  }).userAgentData;
  const mobileIndicators = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /IEMobile/i,
    /Opera Mini/i,
  ];

  const isMobileUA = mobileIndicators.some((regex) => regex.test(userAgent));
  const isMobileUAData = userAgentData?.mobile === true;
  const isMobilePlatform = /Android|iPhone|iPad|iPod/i.test(userAgentData?.platform || '');
  const hasTouch = Boolean(navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  const isAndroidDesktopSite = /Linux/i.test(userAgent) && hasTouch &&
    typeof window.screen?.width === 'number' && window.screen.width <= 1400;
  const isIPadDesktopMode = /Macintosh/i.test(userAgent) &&
    Boolean(navigator.maxTouchPoints && navigator.maxTouchPoints > 1);

  // User-Agent Client Hints preserve the physical platform when Android
  // Chrome is switched to Desktop site, even though userAgent changes.
  return isMobileUA || isMobileUAData || (isMobilePlatform && hasTouch) || isAndroidDesktopSite || isIPadDesktopMode;
};

/**
 * Get device type description
 */
export const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  
  const userAgent = navigator.userAgent;
  const userAgentData = (navigator as Navigator & {
    userAgentData?: { platform?: string };
  }).userAgentData;
  
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Macintosh/i.test(userAgent)) return 'Mac';
  if (/Linux/i.test(userAgent)) return 'Linux';
  if (/Android/i.test(userAgentData?.platform || '')) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgentData?.platform || '')) return 'iOS';
  
  return 'unknown';
};

export interface DeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  deviceModel: string;
  operatingSystem: string;
  browser: string;
  browserVersion: string;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  devicePixelRatio: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  detectionTimestamp: string;
}

const getOperatingSystem = (userAgent: string): string => {
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Unknown';
};

const getBrowserDetails = (userAgent: string): { browser: string; browserVersion: string } => {
  const browserPatterns: Array<[string, RegExp]> = [
    ['Edge', /Edg(?:e|A|iOS)?\/([\d.]+)/i],
    ['Opera', /(?:OPR|Opera)\/([\d.]+)/i],
    ['Samsung Internet', /SamsungBrowser\/([\d.]+)/i],
    ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/i],
    ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/i],
    ['Safari', /Version\/([\d.]+).*Safari/i],
  ];

  for (const [browser, pattern] of browserPatterns) {
    const match = userAgent.match(pattern);
    if (match) return { browser, browserVersion: match[1] || 'Unknown' };
  }

  return { browser: 'Unknown', browserVersion: 'Unknown' };
};

const getDeviceModelFromHints = async (): Promise<string> => {
  const userAgentData = (navigator as Navigator & {
    userAgentData?: {
      getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string; platform?: string }>;
    };
  }).userAgentData;

  if (!userAgentData?.getHighEntropyValues) return 'Unknown';

  try {
    const hints = await userAgentData.getHighEntropyValues(['model']);
    return typeof hints.model === 'string' && hints.model.trim() ? hints.model.trim() : 'Unknown';
  } catch (error) {
    return 'Unknown';
  }
};

const getPlatformFromHints = async (): Promise<string> => {
  const userAgentData = (navigator as Navigator & {
    userAgentData?: {
      platform?: string;
      getHighEntropyValues?: (hints: string[]) => Promise<{ platform?: string }>;
    };
  }).userAgentData;

  if (!userAgentData) return '';
  if (userAgentData.platform) return userAgentData.platform;

  try {
    const hints = await userAgentData.getHighEntropyValues?.(['platform']);
    return hints?.platform || '';
  } catch (error) {
    return '';
  }
};

const getDeviceModelFromUserAgent = (userAgent: string): string => {
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && /Mobile/i.test(userAgent))) return 'iPad';

  const androidMatch = userAgent.match(/Android[^;)]*;\s*([^;)]+?)(?:\s+Build\/[^;)]+)?[;)]/i);
  if (androidMatch?.[1]) {
    const model = androidMatch[1].trim();
    if (model && !/^wv$/i.test(model) && !/^en[-_]/i.test(model)) return model;
  }

  return 'Unknown';
};

export const getDeviceModel = async (): Promise<string> => {
  if (typeof navigator === 'undefined') return 'Unknown';

  const hintedModel = await getDeviceModelFromHints();
  return hintedModel !== 'Unknown' ? hintedModel : getDeviceModelFromUserAgent(navigator.userAgent);
};

export const getDeviceInfo = async (): Promise<DeviceInfo> => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const hintedPlatform = await getPlatformFromHints();
  const mobileOrTablet = isMobileOrTablet();
  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent) || /iPad/i.test(hintedPlatform) || (mobileOrTablet && window.innerWidth > 768);
  const isMobile = mobileOrTablet && !isTablet;
  const browserDetails = getBrowserDetails(userAgent);

  return {
    deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    deviceModel: await getDeviceModel(),
    operatingSystem: getOperatingSystem(userAgent),
    browser: browserDetails.browser,
    browserVersion: browserDetails.browserVersion,
    userAgent,
    screenWidth: typeof window !== 'undefined' ? window.screen.width : 0,
    screenHeight: typeof window !== 'undefined' ? window.screen.height : 0,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
    isMobile,
    isTablet,
    isDesktop: !mobileOrTablet,
    detectionTimestamp: new Date().toISOString(),
  };
};
